<template>
  <modal v-if="display" :title="$t('customization.editTextTitle')">
    <form @submit.prevent="closeModalAfter(() => updateText())">
      <base-textarea
        :ref="ref => (inputRef = ref)"
        v-model="text"
        :autocomplete="false"
        class="mb-2 text-center"
        style="width: 100% !important; text-align: center !important"
        :placeholder="$t('customization.addTextPlaceholder')"
        :disabled="!isFontActive"
      />
      <base-error
        v-show="!allCharactersSupported(text, element.font)"
        class="mb-2"
      >
        {{ $t('warning.charactersNotSupported') }}
      </base-error>
      <base-button
        class="w-full"
        :disabled="
          text.length === 0 ||
          !isFontActive ||
          !allCharactersSupported(text, element.font)
        "
        @click.native="closeModalAfter(() => updateText())"
      >
        {{ $t('customization.editTextButton') }}
      </base-button>
      <alignment-settings-panel :active-element="activeElement" />
      <menu-item to="list-font">
        <div class="flex items-center justify-between">
          <div>
            {{ $t('customization.changeFont') }}
          </div>
          <div class="flex mr-2">
            <img :src="getFontImage" class="self-center h-5" alt="" />
          </div>
        </div>
        <base-error v-if="!isFontActive" class="m-2">
          {{ $t('customization.unavailableFont') }}
        </base-error>
      </menu-item>
      <menu-item
        v-if="hasCapability('color')"
        to="list-color"
        :active="isFontActive"
      >
        <div class="flex items-center justify-between">
          <div>
            {{ $t('customization.changeColor') }}
          </div>
          <div class="flex mr-2 items-center">
            <swatch
              :label="getColorTranslation(element.color)"
              :hex="getColorHex(element.color)"
              class="w-8 h-8 mr-2 ml-0"
            />
            <div v-if="element">
              {{ getColorTranslation(element.color) }}
            </div>
          </div>
        </div>
      </menu-item>
      <menu-item
        v-if="hasCapability('efx')"
        to="change-text-effect"
        :active="isFontActive"
      >
        <div class="flex items-center justify-between">
          <div>
            {{ $t('customization.changeEffect') }}
          </div>
          <div class="flex mr-2 items-center">
            {{ effect.name }}
          </div>
        </div>
      </menu-item>
      <text-effect-config
        v-if="hasCapability('efx')"
        :active-element="activeElement"
      ></text-effect-config>
    </form>
  </modal>
</template>
<script>
import AlignmentSettingsPanel from '../../components/AlignmentSettingsPanel.vue';
import MenuItem from '../../components/MenuItem.vue';
import Modal from '../../components/Modal.vue';
import Swatch from '../../components/Swatch.vue';
import TextEffectConfig from './TextEffectConfig.vue';
import { TrackingMixin } from '../../mixins/gtm-tracking';
import { ACTION_AWAIT_REQUEST } from '../../store/action-types';
import { ACTION_FONT_LOAD_FONTS } from '../../store/modules/font/action-types';
import ColorValues from '../../mixins/color-values';
import Capabilities from '../../mixins/capabilities';
import {
  GETTER_FONT_EFFECT,
  GETTER_FONT_FONT,
  GETTER_FONT_IMAGE
} from '../../store/modules/font/getters';
import { mapGetters } from 'vuex';
import TextChecker from '@/mixins/text-checker';
import ActiveElement from '@/mixins/activeElement';
import Redirect from '@/mixins/redirect';
import { useColorStore } from '@/store-pinia/colors';

export default {
  name: 'EditTextView',
  components: {
    AlignmentSettingsPanel,
    MenuItem,
    Modal,
    Swatch,
    TextEffectConfig
  },
  mixins: [
    TrackingMixin,
    ColorValues,
    Capabilities,
    ActiveElement,
    Redirect,
    TextChecker
  ],
  data: () => ({
    text: '',
    display: false,
    inputRef: null,
    selectAll: true
  }),
  computed: {
    getFontImage() {
      return this.$store.getters[GETTER_FONT_IMAGE](this.element.font);
    },
    isFontActive() {
      return this.$store.getters[GETTER_FONT_FONT](this.element.font).active;
    },
    effect() {
      return this.getEffectById(this.element.effect);
    },
    fontCapabilities() {
      return this.$store.state.font.fonts[this.element.font].capabilities || [];
    },
    ...mapGetters({
      getEffectById: GETTER_FONT_EFFECT
    })
  },
  watch: {
    element() {
      this.text = this.element.text;
    },
    inputRef(val) {
      if (this.selectAll) {
        val.$el.select();
        this.selectAll = false;
      }
    }
  },
  mounted() {
    const colorStore = useColorStore();
    this.$store.dispatch(ACTION_AWAIT_REQUEST, async () => {
      await this.$store.dispatch(ACTION_FONT_LOAD_FONTS);
      await colorStore.loadFontColors();
      this.display = true;
    });

    this.text = this.element.text;
  },
  methods: {
    hasCapability(capability) {
      return this.elementSupports(this.element, capability);
    },
    updateText() {
      this.updateElement({
        text: this.text.replace(/[\n\r]/g, '\n')
      });

      this.trackEvent({
        action: 'UpdateText'
      });
    }
  }
};
</script>
